import React, { useCallback, useEffect, useState } from 'react'

import { TextLink, UiDeleteIcon, UiPlusIcon } from '@octane/spark'
import FormSubsection from 'components/FormSubsection'
import { FieldKit } from 'containers/FormBuilder'
import { setFieldValueAndTouched } from 'form/fields/utils/fieldUtils'
import { FormikValues } from 'formik'
import styled from 'styled-components'

import { FormikCheckbox } from '../../../components/FormikFields/FormikCheckbox/FormikCheckbox.styled'
import { RemoveLink } from '../MarineWriteIn.styled'
import MotorFields from './MotorFields'

interface Props {
    formik: FieldKit<FormikValues>
    prefilled?: boolean
}

const BLANK_ENGINE = {
    condition: 'N',
    year: '',
    make: '',
    model: '',
}

const ElementsContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`

const ActionsRow = styled.div`
    display: flex;
    gap: 30px;
    margin-top: 12px;
`

const SameSecondMotorWrapper = styled.div`
    margin-bottom: 10px;
`

function EngineFormSubsection({ formik, prefilled }: Readonly<Props>) {
    const {
        marine: { engines, repower },
    } = formik.values

    const [sameEngines, setSameEngines] = useState(false)
    const engine0Condition = engines[0]?.condition
    const engine0Year = engines[0]?.year
    const engine0Make = engines[0]?.make
    const engine0Model = engines[0]?.model

    // When sameEngines is checked, keep engine[1] in sync with engine[0]
    useEffect(() => {
        if (sameEngines && engines.length >= 2) {
            setFieldValueAndTouched(formik, 'marine.engines[1]', {
                ...engines[0],
            })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [engine0Condition, engine0Year, engine0Make, engine0Model, sameEngines])

    const handleRemoveEngine = useCallback(
        (index?: number) => {
            if (typeof index === 'number' && !Number.isNaN(index))
                setFieldValueAndTouched(
                    formik,
                    'marine.engines',
                    engines.filter((_: unknown, i: number) => i !== index),
                )
        },
        [formik, engines],
    )

    const handleAddFirstEngine = useCallback(() => {
        setFieldValueAndTouched(formik, 'marine.engines', [{ ...BLANK_ENGINE }])
    }, [formik])

    const handleAddSecondEngine = useCallback(() => {
        setSameEngines(true)

        setFieldValueAndTouched(formik, 'marine.engines', [
            ...engines,
            { ...engines[0] },
        ])
    }, [formik, engines])

    const handleRemoveSecondEngine = useCallback(() => {
        setSameEngines(false)
        handleRemoveEngine(1)
    }, [handleRemoveEngine])

    const handleRemoveAllEngines = useCallback(() => {
        setSameEngines(false)
        setFieldValueAndTouched(formik, 'marine.engines', [])
    }, [formik])

    const handleSameEnginesChange = useCallback(
        (checked: boolean) => {
            setSameEngines(checked)
            if (!checked) {
                setFieldValueAndTouched(formik, 'marine.engines[1]', {
                    ...BLANK_ENGINE,
                })
            }
        },
        [formik],
    )

    if (repower) {
        return (
            <MotorFields
                labelPrefix="Motor"
                engine={engines[0]}
                index={0}
                formik={formik}
                prefilled={prefilled}
            />
        )
    }

    // 0 engines: FormSubsection with "Add Motor" link below header
    if (engines.length === 0) {
        return (
            <FormSubsection title="Motor Details">
                {!prefilled && (
                    <TextLink
                        as="button"
                        endIcon={UiPlusIcon}
                        variant="simple"
                        onClick={handleAddFirstEngine}
                    >
                        Add a Motor
                    </TextLink>
                )}
            </FormSubsection>
        )
    }

    // 1 engine: motor fields + inline action row below
    if (engines.length === 1) {
        return (
            <FormSubsection title="Motor Details">
                <ElementsContainer>
                    <MotorFields
                        engine={engines[0]}
                        index={0}
                        formik={formik}
                        prefilled={prefilled}
                    />
                    {!prefilled && (
                        <ActionsRow>
                            <TextLink
                                as="button"
                                endIcon={UiPlusIcon}
                                variant="simple"
                                onClick={handleAddSecondEngine}
                            >
                                Add a Second Motor
                            </TextLink>

                            <RemoveLink
                                endIcon={UiDeleteIcon}
                                onClick={handleRemoveAllEngines}
                            >
                                Remove Motor
                            </RemoveLink>
                        </ActionsRow>
                    )}
                </ElementsContainer>
            </FormSubsection>
        )
    }

    // 2 engines: both motors, "same as first" checkbox, remove action row
    return (
        <FormSubsection title="Motor Details">
            <ElementsContainer>
                <MotorFields
                    engine={engines[0]}
                    index={0}
                    formik={formik}
                    prefilled={prefilled}
                />
                {!prefilled && (
                    <SameSecondMotorWrapper>
                        <FormikCheckbox
                            id="same-engines"
                            label="The second motor is the same as the first motor."
                            value="same-engines"
                            checked={sameEngines}
                            onChange={handleSameEnginesChange}
                        />
                    </SameSecondMotorWrapper>
                )}
                {!sameEngines && (
                    <MotorFields
                        engine={engines[1]}
                        index={1}
                        formik={formik}
                        prefilled={prefilled}
                    />
                )}
                {!prefilled && (
                    <ActionsRow>
                        <RemoveLink
                            endIcon={UiDeleteIcon}
                            onClick={handleRemoveSecondEngine}
                        >
                            Remove Second Motor
                        </RemoveLink>
                    </ActionsRow>
                )}
            </ElementsContainer>
        </FormSubsection>
    )
}

export default EngineFormSubsection
